import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zhTW } from "date-fns/locale";
import { useNavigate, useParams } from "react-router-dom";
import { MdError, MdKeyboardArrowLeft } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";

import Button from "../Utilities/Button";
import Datepicker from "../Utilities/Datepicker/Datepicker";
import TextField from "../Utilities/TextField";
import Toast from "../Utilities/Toast";
import Alert from "../Utilities/Alert";
import Dropdown from "../Utilities/DropdownDynamic";
import Switcher from "../Utilities/AlertSwitch/Switcher";
import RightPanelLayout from "../Layout/RightPanelLayout";

import EventRichTextEditor from "../EventManagement/EventRichTextEditor";
import SplitHourMinuteTimeInput from "../EventManagement/SplitHourMinuteTimeInput";

import baseStyles from "../EventManagement/style/EventFormPage.module.scss";
import releaseStyles from "./style/ReleaseNoteFormPage.module.scss";
import announcementStyles from "./style/SystemAnnouncementFormPage.module.scss";
import WebsiteAnnouncementPreviewModal from "./WebsiteAnnouncementPreviewModal";
import { stripHtmlToPlain } from "./releaseNotesTransforms";
import {
  buildWebsiteAnnouncementPayload,
  mapWebsiteAnnouncementDetailToFormValues,
} from "./systemAnnouncementsTransforms";
import {
  createWebsiteAnnouncement,
  deleteWebsiteAnnouncement,
  fetchWebsiteAnnouncementById,
  updateWebsiteAnnouncement,
} from "./systemAnnouncementsApi";

const DISPLAY_TYPE_ITEMS = [
  { api: "fullscreen", label: "全螢幕遮罩" },
  { api: "popover", label: "右下角浮動" },
  { api: "banner", label: "底部橫幅" },
];

const DISPLAY_TYPE_LABELS = DISPLAY_TYPE_ITEMS.map((item) => item.label);

const CATEGORY_LABELS = ["系統更新", "發行日記"];

const TITLE_MAX_LENGTH = 40;

const nowLocalForInput = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const isValidRedirectUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return true;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const getAnnouncementTimeOrderWarning = (values) => {
  const startAt = values.announcementStartAt
    ? new Date(values.announcementStartAt)
    : null;
  const endAt = values.announcementEndAt
    ? new Date(values.announcementEndAt)
    : null;
  const hasStart = startAt && !Number.isNaN(startAt.getTime());
  const hasEnd = endAt && !Number.isNaN(endAt.getTime());
  if (!hasStart || !hasEnd) {
    return "";
  }
  if (startAt > endAt) {
    return "上架時間不可晚於下架時間。";
  }
  return "";
};

const SystemAnnouncementFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const initialForm = useMemo(
    () => ({
      title: "",
      content: "",
      announcementStartAt: "",
      announcementEndAt: "",
      displayType: "popover",
      categoryType: "系統更新",
      redirectButtonText: "",
      redirectUrl: "",
      startFeed: false,
      keyFeed: false,
    }),
    [],
  );

  const {
    watch,
    setValue,
    reset,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: initialForm,
  });

  const form = watch();

  const displayTypeLabel = useMemo(() => {
    const found = DISPLAY_TYPE_ITEMS.find((x) => x.api === form.displayType);
    return found?.label ?? "";
  }, [form.displayType]);

  const [detailLoading, setDetailLoading] = useState(mode === "edit");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toolbarSaveWarning, setToolbarSaveWarning] = useState("");
  const [isConfirmLeaveOpen, setIsConfirmLeaveOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [announcementTimeWarning, setAnnouncementTimeWarning] = useState("");

  const showToast = (title, error = false) => {
    setToastState({ show: true, title, subtitle: "", error });
    window.setTimeout(() => {
      setToastState((s) => ({ ...s, show: false }));
    }, 3200);
  };

  useEffect(() => {
    if (mode !== "edit" || !id) {
      setDetailLoading(false);
      reset(initialForm);
      clearErrors();
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    fetchWebsiteAnnouncementById(id)
      .then((data) => {
        if (cancelled) return;
        reset(mapWebsiteAnnouncementDetailToFormValues(data));
        clearErrors();
      })
      .catch(() => {
        if (cancelled) return;
        navigate("/dashboard/tdp/system-announcements");
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, id, reset, clearErrors, navigate, initialForm]);

  useEffect(() => {
    if (mode === "new") {
      reset(initialForm);
      clearErrors();
      setToolbarSaveWarning("");
    }
  }, [mode, initialForm, reset, clearErrors]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setIsConfirmLeaveOpen(true);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isDirty]);

  const startDatePart =
    form.announcementStartAt && form.announcementStartAt.length >= 10
      ? form.announcementStartAt.slice(0, 10)
      : "";

  const startTimePart =
    form.announcementStartAt && form.announcementStartAt.length >= 16
      ? form.announcementStartAt.slice(11, 16)
      : "";

  const endDatePart =
    form.announcementEndAt && form.announcementEndAt.length >= 10
      ? form.announcementEndAt.slice(0, 10)
      : "";

  const endTimePart =
    form.announcementEndAt && form.announcementEndAt.length >= 16
      ? form.announcementEndAt.slice(11, 16)
      : "";

  const announcementStartDateForPicker = useMemo(() => {
    if (!startDatePart) return "";
    const d = new Date(`${startDatePart}T12:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d;
  }, [startDatePart]);

  const announcementEndDateForPicker = useMemo(() => {
    if (!endDatePart) return "";
    const d = new Date(`${endDatePart}T12:00:00`);
    return Number.isNaN(d.getTime()) ? "" : d;
  }, [endDatePart]);

  const handleAnnouncementStartDatePick = (date) => {
    setAnnouncementTimeWarning("");
    const prev = getValues();
    if (!date) {
      setValue("announcementStartAt", "", { shouldDirty: true });
      return;
    }

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const t =
      prev.announcementStartAt && prev.announcementStartAt.length >= 16
        ? prev.announcementStartAt.slice(11, 16)
        : "10:00";
    setValue("announcementStartAt", `${yyyy}-${mm}-${dd}T${t}`, {
      shouldDirty: true,
    });
  };

  const onAnnouncementStartTimeCommit = (hhmm) => {
    setAnnouncementTimeWarning("");
    const t = hhmm || "00:00";
    const prev = getValues();
    const date =
      prev.announcementStartAt && prev.announcementStartAt.length >= 10
        ? prev.announcementStartAt.slice(0, 10)
        : nowLocalForInput().slice(0, 10);
    setValue("announcementStartAt", `${date}T${t}`, { shouldDirty: true });
  };

  const handleAnnouncementEndDatePick = (date) => {
    setAnnouncementTimeWarning("");
    const prev = getValues();
    if (!date) {
      setValue("announcementEndAt", "", { shouldDirty: true });
      return;
    }

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const t =
      prev.announcementEndAt && prev.announcementEndAt.length >= 16
        ? prev.announcementEndAt.slice(11, 16)
        : "10:00";
    setValue("announcementEndAt", `${yyyy}-${mm}-${dd}T${t}`, {
      shouldDirty: true,
    });
  };

  const onAnnouncementEndTimeCommit = (hhmm) => {
    setAnnouncementTimeWarning("");
    const t = hhmm || "00:00";
    const prev = getValues();
    const date =
      prev.announcementEndAt && prev.announcementEndAt.length >= 10
        ? prev.announcementEndAt.slice(0, 10)
        : nowLocalForInput().slice(0, 10);
    setValue("announcementEndAt", `${date}T${t}`, { shouldDirty: true });
  };

  const setField = (key, value) => {
    setValue(key, value, { shouldDirty: true });
    if (key === "redirectUrl" || (value !== "" && value != null)) {
      clearErrors(key);
    }
    if (key === "title" && toolbarSaveWarning) {
      setToolbarSaveWarning("");
    }
  };

  const handleBack = () => {
    if (!isDirty) {
      navigate("/dashboard/tdp/system-announcements");
      return;
    }
    setIsConfirmLeaveOpen(true);
  };

  const handleLeaveConfirm = () => {
    setIsConfirmLeaveOpen(false);
    navigate("/dashboard/tdp/system-announcements");
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    setToolbarSaveWarning("");
    setAnnouncementTimeWarning("");
    clearErrors();

    const title = String(form.title || "").trim();
    if (!title) {
      setError("title", { type: "manual", message: "此欄位為必填" });
      setToolbarSaveWarning("未輸入標題");
      return;
    }

    if (title.length > TITLE_MAX_LENGTH) {
      setError("title", {
        type: "manual",
        message: `標題不可超過 ${TITLE_MAX_LENGTH} 字`,
      });
      setToolbarSaveWarning(`標題不可超過 ${TITLE_MAX_LENGTH} 字`);
      return;
    }

    const plainContent = stripHtmlToPlain(form.content || "");
    if (!plainContent.trim()) {
      setError("content", { type: "manual", message: "此欄位為必填" });
      setToolbarSaveWarning("未輸入內文介紹");
      return;
    }

    if (!form.announcementStartAt) {
      setError("announcementStartAt", {
        type: "manual",
        message: "請選擇公告開始時間",
      });
      setToolbarSaveWarning("未設定公告時間");
      return;
    }

    const timeOrderWarning = getAnnouncementTimeOrderWarning(form);
    if (timeOrderWarning) {
      setError("announcementStartAt", {
        type: "manual",
        message: "時間設定有誤",
      });
      setError("announcementEndAt", {
        type: "manual",
        message: "時間設定有誤",
      });
      setAnnouncementTimeWarning(timeOrderWarning);
      return;
    }

    const redirectUrl = String(form.redirectUrl || "").trim();
    if (!isValidRedirectUrl(redirectUrl)) {
      setError("redirectUrl", {
        type: "manual",
        message: "請輸入有效網址（http:// 或 https://）",
      });
      setToolbarSaveWarning("導連網址格式不正確");
      return;
    }

    const payload = buildWebsiteAnnouncementPayload({
      title: form.title,
      content: form.content,
      announcementStartLocal: form.announcementStartAt,
      announcementEndLocal: form.announcementEndAt,
      displayType: form.displayType,
      categoryLabel: form.categoryType,
      redirectButtonText: form.redirectButtonText,
      redirectUrl: form.redirectUrl,
      startFeed: form.startFeed,
      keyFeed: form.keyFeed,
    });

    if (!payload) {
      showToast("儲存失敗請重試", true);
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === "new") {
        await createWebsiteAnnouncement(payload);
      } else {
        await updateWebsiteAnnouncement(id, payload);
      }

      showToast(`已儲存公告「${title}」。`, false);
      navigate("/dashboard/tdp/system-announcements");
    } catch (err) {
      showToast(err?.message || "儲存失敗請重試", true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleteOpen(false);
    try {
      await deleteWebsiteAnnouncement(id);
      showToast("已刪除系統公告", false);
      navigate("/dashboard/tdp/system-announcements");
    } catch (err) {
      showToast(err?.message || "刪除失敗請重試", true);
    }
  };

  if (detailLoading) {
    return (
      <RightPanelLayout title="系統公告">
        <div className={releaseStyles.previewSaveHint}>載入中…</div>
      </RightPanelLayout>
    );
  }

  return (
    <RightPanelLayout title="系統公告">
      <form
        className={baseStyles.form}
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div className={baseStyles.topToolbarHeader}>
          <button
            type='button'
            className={baseStyles.backButton}
            onClick={handleBack}
          >
            <MdKeyboardArrowLeft size={32} />
            返回
          </button>

          <div className={baseStyles.rightActions}>
            {toolbarSaveWarning ? (
              <span className={baseStyles.toolbarWarningText}>
                <MdError size={22} aria-hidden />
                {toolbarSaveWarning}
              </span>
            ) : null}

            {announcementTimeWarning ? (
              <span className={baseStyles.toolbarWarningText}>
                <MdError size={22} aria-hidden />
                {announcementTimeWarning}
              </span>
            ) : null}

            <button
              type='button'
              className={baseStyles.previewButton}
              onClick={() => setIsPreviewOpen(true)}
            >
              預覽
            </button>

            <Button
              type='primary'
              title={isSubmitting ? "儲存中…" : "儲存"}
              disabled={isSubmitting}
            />

            {mode === "edit" ? (
              <button
                type='button'
                className={baseStyles.deleteButton}
                onClick={() => setIsDeleteOpen(true)}
                aria-label='刪除公告'
              >
                <FiTrash2 size={24} aria-hidden />
                刪除公告
              </button>
            ) : null}
          </div>
        </div>

        <div className={releaseStyles.whiteCard}>
          <div className={baseStyles.columnStack}>
            <div className={baseStyles.infoRow}>
              <div
                className={`${baseStyles.infoRowLabel} ${announcementStyles.alignedLabel}`}
              >
                公告標題
                <span className={baseStyles.requiredPrimary}>*</span>
              </div>
              <div className={baseStyles.infoRowBody}>
                <TextField
                  placeholder='請輸入'
                  width='100%'
                  fullWidth
                  value={form.title}
                  error={Boolean(errors.title)}
                  onChange={(v) => setField("title", v)}
                />
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div
                className={`${baseStyles.infoRowLabel} ${announcementStyles.alignedLabel}`}
              >
                公告時間
              </div>
              <div className={baseStyles.infoRowBody}>
                <div className={baseStyles.activityTimeControls}>
                  <div className={baseStyles.activityDatePickerWrap}>
                    <Datepicker
                      selectDate={announcementStartDateForPicker}
                      setSelectDate={handleAnnouncementStartDatePick}
                      error={Boolean(errors.announcementStartAt)}
                      iconType='arrow'
                      placeholderText='選擇日期'
                      dateFormat='yyyy-MM-dd'
                      locale={zhTW}
                    />
                  </div>
                  <SplitHourMinuteTimeInput
                    value={startTimePart || "10:00"}
                    onCommit={onAnnouncementStartTimeCommit}
                    ariaLabel='公告開始時間'
                  />
                  <span className={baseStyles.infoTimeSep} aria-hidden>
                    -
                  </span>
                  <div className={baseStyles.activityDatePickerWrap}>
                    <Datepicker
                      selectDate={announcementEndDateForPicker}
                      setSelectDate={handleAnnouncementEndDatePick}
                      error={Boolean(errors.announcementEndAt)}
                      iconType='arrow'
                      placeholderText='選擇日期'
                      dateFormat='yyyy-MM-dd'
                      locale={zhTW}
                    />
                  </div>
                  <SplitHourMinuteTimeInput
                    value={endTimePart || "10:00"}
                    onCommit={onAnnouncementEndTimeCommit}
                    ariaLabel='公告結束時間'
                  />
                </div>
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div
                className={`${baseStyles.infoRowLabel} ${announcementStyles.alignedLabel}`}
              >
                公告顯示類型
              </div>
              <div className={baseStyles.infoRowBody}>
                <div className={announcementStyles.dropdownWrap}>
                  <Dropdown
                    placeholder='請選擇'
                    value={displayTypeLabel}
                    listItems={DISPLAY_TYPE_LABELS}
                    fitContent={false}
                    color='#333333'
                    onChange={(item) => {
                      const label = String(item || "");
                      const found = DISPLAY_TYPE_ITEMS.find(
                        (x) => x.label === label,
                      );
                      setField("displayType", found?.api ?? "popover");
                    }}
                  />
                </div>
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div
                className={`${baseStyles.infoRowLabel} ${announcementStyles.alignedLabel}`}
              >
                類型
              </div>
              <div className={baseStyles.infoRowBody}>
                <div className={announcementStyles.dropdownWrap}>
                  <Dropdown
                    placeholder='請選擇'
                    value={form.categoryType}
                    listItems={CATEGORY_LABELS}
                    fitContent={false}
                    color='#333333'
                    onChange={(item) =>
                      setField("categoryType", String(item || ""))
                    }
                  />
                </div>
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div
                className={`${baseStyles.infoRowLabel} ${announcementStyles.alignedLabel}`}
              >
                上架於
              </div>
              <div className={baseStyles.infoRowBody}>
                <div className={announcementStyles.feedSwitchRow}>
                  <div className={announcementStyles.feedSwitchItem}>
                    <span className={announcementStyles.feedSwitchLabel}>
                      Keystone
                    </span>
                    <Switcher
                      isChecked={form.keyFeed}
                      onChange={() => setField("keyFeed", !form.keyFeed)}
                    />
                  </div>
                  <div className={announcementStyles.feedSwitchItem}>
                    <span className={announcementStyles.feedSwitchLabel}>
                      START-ONE
                    </span>
                    <Switcher
                      isChecked={form.startFeed}
                      onChange={() => setField("startFeed", !form.startFeed)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={announcementStyles.contentIntroBlock}>
              <div className={announcementStyles.contentIntroLabel}>
                內文介紹
              </div>
              <div
                className={
                  errors.content ? baseStyles.richTextErrorWrap : undefined
                }
              >
                <EventRichTextEditor
                  value={form.content}
                  onChange={(html) => setField("content", html)}
                />
              </div>
            </div>

            <div className={baseStyles.infoRow}>
              <div
                className={`${baseStyles.infoRowLabel} ${announcementStyles.alignedLabel}`}
              >
                導連按鈕
              </div>
              <div className={baseStyles.infoRowBody}>
                <div className={announcementStyles.redirectFieldStack}>
                  <div className={announcementStyles.redirectButtonTextField}>
                    <TextField
                      placeholder='請輸入按鈕文字'
                      width='100%'
                      fullWidth
                      value={form.redirectButtonText}
                      onChange={(v) => setField("redirectButtonText", v)}
                    />
                  </div>
                  <TextField
                    placeholder='https://'
                    width='100%'
                    fullWidth
                    value={form.redirectUrl}
                    error={Boolean(errors.redirectUrl)}
                    onChange={(v) => setField("redirectUrl", v)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <Alert
        isOpen={isConfirmLeaveOpen}
        onClose={() => setIsConfirmLeaveOpen(false)}
        onConfirm={handleLeaveConfirm}
        leftButtonText='留在此頁'
        rightButtonText='離開'
        mainTitle='尚未儲存，確定離開？'
        subTitle='離開後本次填寫的內容將不會保留。'
        isWarning
      />

      <WebsiteAnnouncementPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title={form.title}
        contentHtml={form.content}
        displayType={form.displayType}
        redirectButtonText={form.redirectButtonText}
        redirectUrl={form.redirectUrl}
      />

      <Alert
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        leftButtonText='取消'
        rightButtonText='刪除'
        mainTitle='確認刪除系統公告？'
        subTitle='刪除後無法復原。'
        isWarning
      />

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default SystemAnnouncementFormPage;
